import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

/**
 * Generate a dark-themed PDF report for an OSINT search.
 * @param {{ searchType: string, searchTerm: string|File, stats: object, tools: Array }} options
 */
export const generatePDFReport = ({ searchType, searchTerm, stats, tools = [] }) => {
  const displayTerm = searchTerm instanceof File ? searchTerm.name : String(searchTerm);
  const safeName = displayTerm.replace(/[^a-zA-Z0-9_.-]/g, '_').slice(0, 60);
  const doc = new jsPDF();
  
  // Override addPage to ensure dark background on all subsequent pages
  const originalAddPage = doc.addPage;
  doc.addPage = function() {
    originalAddPage.apply(this, arguments);
    doc.setFillColor(15, 12, 41); // #0f0c29 (App primary dark bg)
    doc.rect(0, 0, doc.internal.pageSize.getWidth(), doc.internal.pageSize.getHeight(), 'F');
    return this;
  };

  // Draw initial page background
  doc.setFillColor(15, 12, 41);
  doc.rect(0, 0, doc.internal.pageSize.getWidth(), doc.internal.pageSize.getHeight(), 'F');

  const logoImg = new Image();
  logoImg.src = '/az_logo.png';
  let isAdded = false;

  const buildReport = () => {
    if (isAdded) return;
    isAdded = true;

    doc.setFontSize(22);
    doc.setTextColor(255, 255, 255);
    doc.text('OSINT Hub — Intelligence Report', 14, (logoImg.complete && logoImg.naturalHeight > 0) ? 45 : 20);

    doc.setFontSize(12);
    doc.setTextColor(156, 163, 175);
    const startY = (logoImg.complete && logoImg.naturalHeight > 0) ? 55 : 30;
    doc.text(`Search Term: ${displayTerm}`, 14, startY);
    doc.text(`Search Type: ${searchType.toUpperCase()}`, 14, startY + 7);
    doc.text(`Generated: ${new Date().toLocaleString()}`, 14, startY + 14);
    doc.text(`Total Platforms: ${stats.total}`, 14, startY + 21);

    doc.setTextColor(167, 139, 250);
    doc.text(`Found on: ${stats.found} platforms`, 14, startY + 28);

    // Custom formatting based on search type
    let head = [];
    let tableData = [];
    let columnStyles = {};

    switch (searchType.toLowerCase()) {
      case 'username':
      case 'social':
        head = [['Platform', 'Category', 'Status', 'Profile/Details']];
        tableData = tools.map((t) => {
          let status = 'Not Found';
          if (t.result?.found) {
            status = t.result.manual ? 'Check Manually' : 'Found';
          } else if (t.result?.error) {
            status = 'Error';
          }

          let details = '';
          if (t.result?.found && t.result.data) {
            details = Object.entries(t.result.data)
              .map(([k, v]) => `${k}: ${String(v).replace(/<[^>]*>?/gm, '')}`)
              .join('\n');
          } else if (t.result?.error) {
            details = t.result.error;
          }
          return [t.name, t.category, status, details];
        });
        columnStyles = { 0: { cellWidth: 35 }, 1: { cellWidth: 30 }, 2: { cellWidth: 30 }, 3: { cellWidth: 'auto' } };
        break;

      case 'email':
        head = [['Service', 'Category', 'Status', 'Validation/Breach Details']];
        tableData = tools.map((t) => {
          let status = t.result?.found ? 'Found/Valid' : (t.result?.error ? 'Error' : 'Not Found/Invalid');
          let details = '';
          if (t.result?.found && t.result.data) {
             details = Object.entries(t.result.data)
               .map(([k, v]) => `${k}: ${typeof v === 'object' ? JSON.stringify(v) : String(v).replace(/<[^>]*>?/gm, '')}`)
               .join('\n');
          } else if (t.result?.error) {
             details = t.result.error;
          }
          return [t.name, t.category, status, details];
        });
        columnStyles = { 0: { cellWidth: 35 }, 1: { cellWidth: 30 }, 2: { cellWidth: 30 }, 3: { cellWidth: 'auto' } };
        break;

      case 'phone':
        head = [['Service', 'Category', 'Status', 'Phone Details/Links']];
        tableData = tools.map((t) => {
          let status = 'Not Found';
          if (t.result?.found) {
            status = t.result.manual ? 'Check Manually' : 'Found/Valid';
          } else if (t.result?.error) {
            status = 'Error';
          }
          
          let details = '';
          if (t.result?.found && t.result.data) {
             details = Object.entries(t.result.data)
               .map(([k, v]) => `${k}: ${typeof v === 'object' ? JSON.stringify(v) : String(v).replace(/<[^>]*>?/gm, '')}`)
               .join('\n');
          } else if (t.result?.error) {
             details = t.result.error;
          }
          return [t.name, t.category, status, details];
        });
        columnStyles = { 0: { cellWidth: 35 }, 1: { cellWidth: 30 }, 2: { cellWidth: 30 }, 3: { cellWidth: 'auto' } };
        break;

      case 'domain':
        head = [['Domain Check', 'Status', 'Records/Details']];
        tableData = tools.map((t) => {
          let status = t.result?.found ? 'Data Available' : (t.result?.error ? 'Error' : 'No Data');
          let details = '';
          if (t.result?.found && t.result.data) {
             details = Object.entries(t.result.data)
               .map(([k, v]) => {
                  let valStr = '';
                  if (typeof v === 'object' && v !== null) {
                    try { valStr = JSON.stringify(v, null, 2); } catch(e) { valStr = String(v); }
                  } else {
                    valStr = String(v);
                  }
                  return `${k}:\n${valStr}`;
               })
               .join('\n\n');
          } else if (t.result?.error) {
             details = t.result.error;
          }
          return [t.name, status, details];
        });
        columnStyles = { 0: { cellWidth: 40 }, 1: { cellWidth: 30 }, 2: { cellWidth: 'auto' } };
        break;

      case 'image':
      case 'video':
        head = [['Search Engine', 'Category', 'Status', 'Matches/Metadata']];
        tableData = tools.map((t) => {
          let status = t.result?.found ? 'Match Found' : (t.result?.error ? 'Error' : 'No Match');
          let details = '';
          if (t.result?.found && t.result.data) {
             details = Object.entries(t.result.data)
               .map(([k, v]) => `${k}: ${String(v).replace(/<[^>]*>?/gm, '')}`)
               .join('\n');
          } else if (t.result?.error) {
             details = t.result.error;
          }
          return [t.name, t.category, status, details];
        });
        columnStyles = { 0: { cellWidth: 35 }, 1: { cellWidth: 30 }, 2: { cellWidth: 30 }, 3: { cellWidth: 'auto' } };
        break;

      case 'ai_report': {
        const aiTool = tools.find(t => t.name === 'AI Web Report');
        const aiData = aiTool?.result?.data;
        
        if (!aiData || aiTool?.result?.error || Object.keys(aiData).length === 0) {
           head = [['Platform', 'Status', 'Details']];
           tableData = [[aiTool?.name || 'AI Report', 'Error', aiTool?.result?.error || 'No data found']];
           columnStyles = { 0: { cellWidth: 40 }, 1: { cellWidth: 30 }, 2: { cellWidth: 'auto' } };
           break;
        }

        let currentY = startY + 45;
        
        const renderSection = (title, content) => {
           if (!content || (Array.isArray(content) && content.length === 0)) return;
           
           // Check page break before title
           if (currentY > 260) {
             doc.addPage();
             currentY = 20;
           }

           doc.setFont(undefined, 'bold');
           doc.setFontSize(14);
           doc.setTextColor(167, 139, 250);
           doc.text(title, 14, currentY);
           currentY += 8;
           
           doc.setFont(undefined, 'normal');
           doc.setFontSize(11);
           doc.setTextColor(226, 232, 240);
           
           if (Array.isArray(content)) {
             content.forEach(item => {
               // Check page break before item
               if (currentY > 275) {
                 doc.addPage();
                 currentY = 20;
               }
               const lines = doc.splitTextToSize(`• ${item}`, 182);
               doc.text(lines, 14, currentY);
               currentY += (lines.length * 5) + 2;
             });
           } else {
             const lines = doc.splitTextToSize(String(content), 182);
             
             // If a single text block is very long, we should ideally handle page breaks within it, 
             // but for simplicity we will just draw it and if it goes over we can live with some cutoff 
             // or rely on auto addPage if we did it properly. Let's do a simple line-by-line render.
             lines.forEach(line => {
               if (currentY > 280) {
                 doc.addPage();
                 currentY = 20;
               }
               doc.text(line, 14, currentY);
               currentY += 5;
             });
           }
           currentY += 8;
        };

        renderSection('Executive Summary', aiData['Summary']);
        renderSection('Key Insights', aiData['Key Points']);
        renderSection('Notable Entities', aiData['Entities Identified']);
        renderSection('Conclusion', aiData['Conclusion']);
        renderSection('Sources Consulted', aiData['Sources Extracted'] || aiData['Sources Used']);
        
        doc.save(`AI_Web_Intel_${safeName}.pdf`);
        return; // Early return to avoid autoTable
      }

      default:
        head = [['Platform', 'Category', 'Status', 'Details']];
        tableData = tools.map((t) => {
          let status = 'Not Found';
          if (t.result?.found) {
              status = t.result.manual ? 'Check Manually' : 'Found';
          } else if (t.result?.error) {
              status = 'Error';
          }
          let details = '';
          if (t.result?.found && t.result.data) {
             details = Object.entries(t.result.data)
               .map(([k, v]) => `${k}: ${String(v).replace(/<[^>]*>?/gm, '')}`)
               .join('\n');
          } else if (t.result?.error) {
             details = t.result.error;
          }
          return [t.name, t.category, status, details];
        });
        columnStyles = { 0: { cellWidth: 35 }, 1: { cellWidth: 30 }, 2: { cellWidth: 30 }, 3: { cellWidth: 'auto' } };
        break;
    }

    autoTable(doc, {
      startY: startY + 30,
      head: head,
      body: tableData,
      theme: 'grid',
      styles: { 
        fontSize: 9, 
        cellPadding: 4, 
        overflow: 'linebreak',
        fillColor: [30, 41, 59], // bg-slate-800
        textColor: [241, 245, 249], // text-slate-100
        lineColor: [51, 65, 85], // border-slate-700
        lineWidth: 0.1
      },
      headStyles: { 
        fillColor: [79, 70, 229], // bg-indigo-500
        textColor: 255,
        fontStyle: 'bold'
      },
      alternateRowStyles: {
        fillColor: [15, 23, 42] // bg-slate-900
      },
      columnStyles: columnStyles
    });
    
    doc.save(`OSINT_Report_${safeName}_${searchType}.pdf`);
  };

  logoImg.onload = () => {
    try {
      doc.addImage(logoImg, 'PNG', 14, 10, 30, 30);
    } catch (_) {
      // Logo load error — continue without it
    }
    buildReport();
  };

  // Fall back immediately if logo fails or takes too long (200ms)
  let reported = false;
  const fallback = setTimeout(() => {
    if (!reported) buildReport();
  }, 200);

  logoImg.onerror = () => {
    clearTimeout(fallback);
    if (!reported) buildReport();
  };

  logoImg.onload = () => {
    clearTimeout(fallback);
    try { doc.addImage(logoImg, 'PNG', 14, 10, 30, 30); } catch (_) {}
    if (!reported) buildReport();
  };
};
